import "./App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import SplashScreen from "./components/SplashScreen";
import HomePage from "./pages/HomePage";
import SearchPage from "./pages/SearchPage";
import ProfileDetailPage from "./pages/ProfileDetailPage";
import ProfilePage from "./pages/ProfilePage";
import EditProfilePage from "./pages/EditProfilePage";
import ConnectionsPage from "./pages/ConnectionsPage";
import InterestsPageNew from "./pages/InterestsPageNew";
import ShortlistPage from "./pages/ShortlistPage";
import MessagesPageNew from "./pages/MessagesPageNew";
import ChatPage from "./pages/ChatPage";
import AccountSettingsPage from "./pages/AccountSettingsPage";
import PricingPage from "./pages/PricingPage";
import SubscriptionDetailsPage from "./pages/SubscriptionDetailsPage";
import LoginPage from "./pages/LoginPage";
import RegisterPageNew from "./pages/RegisterPageNew";
import EmailVerificationPage from "./pages/EmailVerificationPage";
import ProfileCompletionPage from "./pages/ProfileCompletionPage";
import CompleteProfilePage from "./pages/CompleteProfilePage";
import VerificationPendingPage from "./pages/VerificationPendingPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PaymentPageStripe from "./pages/PaymentPageStripe";
import PaymentPagePayPal from "./pages/PaymentPagePayPal";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import AboutPage from "./pages/AboutPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import ContactPage from "./pages/ContactPage";
import DonationPage from "./pages/DonationPage";
import HelpPage from "./pages/HelpPage";
import AdminLayout from "./pages/AdminLayout";
import AdminDashboard from "./pages/AdminDashboardNew";
import AdminCancellationsRefunds from "./pages/AdminCancellationsRefunds";
import AdminUsers from "./pages/AdminUsers";
import SupportMessagesPage from "./pages/SupportMessagesPage";
import AdminUserDetail from "./pages/AdminUserDetail";
import AdminAccountApprovals from "./pages/AdminAccountApprovals";
import AdminReports from "./pages/AdminReports";
import AdminVerifications from "./pages/AdminVerifications";
import AdminIdentityVerifications from "./pages/AdminIdentityVerifications";
import AdminMessages from "./pages/AdminMessages";
import AdminAnnouncements from "./pages/AdminAnnouncements";
import AdminPaymentSettings from "./pages/AdminPaymentSettings";
import AdminTrialReminders from "./pages/AdminTrialReminders";
import AdminInactiveUsers from "./pages/AdminInactiveUsers";
import AdminSiteSettings from "./pages/AdminSiteSettings";
import AdminContentManagement from "./pages/AdminContentManagement";
import AdminPromoCodes from "./pages/AdminPromoCodes";
import AdminSuccessStories from "./pages/AdminSuccessStories";
import AdminEmailTemplates from "./pages/AdminEmailTemplates";
import AdminMaintenance from "./pages/AdminMaintenance";
import AdminBulkMessaging from "./pages/AdminBulkMessaging";
import AdminActivityLogs from "./pages/AdminActivityLogs";
import AdminSEO from "./pages/AdminSEO";
import AdminHomepageBanners from "./pages/AdminHomepageBanners";
import AdminRoles from "./pages/AdminRoles";
import AdminDataExport from "./pages/AdminDataExport";
import AdminBackups from "./pages/AdminBackups";
import AdminMatchingAlgorithm from "./pages/AdminMatchingAlgorithm";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminProfileFields from "./pages/AdminProfileFields";
import AdminNotificationSettings from "./pages/AdminNotificationSettings";
import AdminScheduledAnnouncements from "./pages/AdminScheduledAnnouncements";
import AdminProfileCardDesigner from "./pages/AdminProfileCardDesigner";
import AdminHomepageSections from "./pages/AdminHomepageSections";
import AdminFooterSettings from "./pages/AdminFooterSettings";
import AdminNavigationMenu from "./pages/AdminNavigationMenu";
import AdminThemeDesigner from "./pages/AdminThemeDesigner";
import AdminWebsiteDesigner from "./pages/AdminWebsiteDesigner";
import ShopPage from "./pages/ShopPage";
import ShopProductDetailPage from "./pages/ShopProductDetailPage";
import ShopCartPage from "./pages/ShopCartPage";
import ShopOrderSuccessPage from "./pages/ShopOrderSuccessPage";
import AdminShop from "./pages/AdminShop";
import AdminShopProducts from "./pages/AdminShopProducts";
import AdminShopCategories from "./pages/AdminShopCategories";
import AdminShopOrders from "./pages/AdminShopOrders";
import AdminShopSettings from "./pages/AdminShopSettings";
import VerificationPage from "./pages/VerificationPage";
import BasicInfoPage from "./pages/BasicInfoPage";
import AccountPendingPage from "./pages/AccountPendingPage";
import { Toaster } from "./components/ui/toaster";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { RefreshProvider } from "./context/RefreshContext";
import VerificationGuard from "./components/VerificationGuard";
import WhatsAppButton from "./components/WhatsAppButton";
import AnnouncementsContainer from "./components/AnnouncementsContainer";
import UpdateBanner from "./components/UpdateBanner";
import PullToRefresh from "./components/PullToRefresh";

function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  useEffect(() => {
    // Only show splash on first app load (not on page navigations)
    const hasSeenSplash = sessionStorage.getItem('hasSeenSplash');
    if (hasSeenSplash) {
      setShowSplash(false);
      setIsFirstLoad(false);
    }
  }, []);

  const handleSplashComplete = () => {
    setShowSplash(false);
    sessionStorage.setItem('hasSeenSplash', 'true');
  };

  return (
    <div className="App">
      {showSplash && isFirstLoad && <SplashScreen onComplete={handleSplashComplete} />}
      <ThemeProvider>
        <RefreshProvider>
          <AuthProvider>
            <BrowserRouter>
              <ScrollToTop />
              <UpdateBanner />
              <Routes>
                {/* Admin Routes - No Navbar/Footer */}
                <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="users/:userId" element={<AdminUserDetail />} />
              <Route path="account-approvals" element={<AdminAccountApprovals />} />
              <Route path="identity-verifications" element={<AdminIdentityVerifications />} />
              <Route path="announcements" element={<AdminAnnouncements />} />
              <Route path="payment-settings" element={<AdminPaymentSettings />} />
              <Route path="trial-reminders" element={<AdminTrialReminders />} />
              <Route path="inactive-reminders" element={<AdminInactiveUsers />} />
              <Route path="site-settings" element={<AdminSiteSettings />} />
              <Route path="content" element={<AdminContentManagement />} />
              <Route path="promo-codes" element={<AdminPromoCodes />} />
              <Route path="success-stories" element={<AdminSuccessStories />} />
              <Route path="email-templates" element={<AdminEmailTemplates />} />
              <Route path="maintenance" element={<AdminMaintenance />} />
              <Route path="bulk-messaging" element={<AdminBulkMessaging />} />
              <Route path="activity-logs" element={<AdminActivityLogs />} />
              <Route path="seo" element={<AdminSEO />} />
              <Route path="homepage-banners" element={<AdminHomepageBanners />} />
              <Route path="roles" element={<AdminRoles />} />
              <Route path="data-export" element={<AdminDataExport />} />
              <Route path="backups" element={<AdminBackups />} />
              <Route path="matching-algorithm" element={<AdminMatchingAlgorithm />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="profile-fields" element={<AdminProfileFields />} />
              <Route path="notification-settings" element={<AdminNotificationSettings />} />
              <Route path="scheduled-announcements" element={<AdminScheduledAnnouncements />} />
              <Route path="card-designer" element={<AdminProfileCardDesigner />} />
              <Route path="homepage-sections" element={<AdminHomepageSections />} />
              <Route path="footer-settings" element={<AdminFooterSettings />} />
              <Route path="navigation-menu" element={<AdminNavigationMenu />} />
              <Route path="theme-designer" element={<Navigate to="/admin/website-designer" replace />} />
              <Route path="website-designer" element={<AdminWebsiteDesigner />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="messages" element={<AdminMessages />} />
              <Route path="cancellations" element={<AdminCancellationsRefunds />} />
              <Route path="shop" element={<AdminShop />} />
              <Route path="shop/products" element={<AdminShopProducts />} />
              <Route path="shop/categories" element={<AdminShopCategories />} />
              <Route path="shop/orders" element={<AdminShopOrders />} />
              <Route path="shop/settings" element={<AdminShopSettings />} />
            </Route>

            {/* Main App Routes - With Navbar/Footer */}
            <Route path="/*" element={
              <VerificationGuard>
                <AnnouncementsContainer />
                <Navbar />
                <PullToRefresh>
                  <div className="pt-16">
                    <Routes>
                    <Route path="/" element={<HomePage />} />
                  <Route path="/search" element={<SearchPage />} />
                  <Route path="/profile/:id" element={<ProfileDetailPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/edit-profile" element={<EditProfilePage />} />
                  <Route path="/connections" element={<ConnectionsPage />} />
                  <Route path="/interests" element={<InterestsPageNew />} />
                  <Route path="/shortlist" element={<ShortlistPage />} />
                  <Route path="/messages" element={<MessagesPageNew />} />
                  <Route path="/chat/:userId" element={<ChatPage />} />
                  <Route path="/messages/:userId" element={<ChatPage />} />
                  <Route path="/support-messages" element={<SupportMessagesPage />} />
                  <Route path="/account-settings" element={<AccountSettingsPage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/subscription" element={<SubscriptionDetailsPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPageNew />} />
                  <Route path="/verify-email" element={<EmailVerificationPage />} />
                  <Route path="/basic-info" element={<BasicInfoPage />} />
                  <Route path="/account-pending" element={<AccountPendingPage />} />
                  <Route path="/complete-profile" element={<CompleteProfilePage />} />
                  <Route path="/verification-pending" element={<VerificationPendingPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/profile-completion" element={<ProfileCompletionPage />} />
                  <Route path="/verification" element={<VerificationPage />} />
                  <Route path="/payment" element={<PaymentPagePayPal />} />
                  <Route path="/payment-stripe" element={<PaymentPageStripe />} />
                  <Route path="/payment-success" element={<PaymentSuccessPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/donation" element={<DonationPage />} />
                  <Route path="/help" element={<HelpPage />} />
                  <Route path="/shop" element={<ShopPage />} />
                  <Route path="/shop/product/:id" element={<ShopProductDetailPage />} />
                  <Route path="/shop/cart" element={<ShopCartPage />} />
                  <Route path="/shop/order-success" element={<ShopOrderSuccessPage />} />
                    </Routes>
                    <Footer />
                  </div>
                </PullToRefresh>
              </VerificationGuard>
            } />
          </Routes>
          <Toaster />
          <WhatsAppButton />
        </BrowserRouter>
      </AuthProvider>
      </RefreshProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
